import type { RouterOutputs } from "@api/trpc/routers/_app";
import type { UIMessage } from "ai";
import { type ClassValue, clsx } from "clsx";
import { franc } from "franc-min";
import type { Metadata } from "next";
import slugify from "slugify";
import { twMerge } from "tailwind-merge";
import { z } from "zod";
import type { SearchContext } from "@/features/search/types";
import { createClient } from "@/utils/supabase/client";
import type { QuestionStatus } from "./types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const apiURL = process.env.NEXT_PUBLIC_API_URL
  ? `https://${process.env.NEXT_PUBLIC_API_URL}`
  : "http://localhost:3000";

export function getTextFromMessage(message: UIMessage): string {
  return message.parts
    .filter((part) => part.type === "text")
    .map((part) => part.text)
    .join("");
}

/**
 * Helper function to convert PDF coordinates to react-pdf coordinates
 * react-pdf uses a different coordinate system
 */
export function convertToReactPDFCoords(
  bbox: { x: number; y: number; width: number; height: number },
  pageHeight: number,
  scale = 1,
): { x: number; y: number; width: number; height: number } {
  return {
    x: bbox.x * scale,
    y: (pageHeight - bbox.y - bbox.height) * scale, // Flip Y axis
    width: bbox.width * scale,
    height: (bbox.height + 5) * scale,
  };
}

export function generateUUID(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export async function getAuthToken() {
  const supabase = createClient();
  const { data, error } = await supabase.auth.getSession();
  if (error) {
    console.error("Error getting auth token", error);
    return undefined;
  }
  return data.session?.access_token;
}

export function detectLanguage(text: string): "english" | "spanish" | null {
  const langCode = franc(text, { minLength: 10 }); // need a bit of text to be accurate

  if (langCode === "spa") return "spanish";
  if (langCode === "eng") return "english";

  // Default to English if unsure
  return null;
}

export function absoluteUrl(path: string) {
  if (typeof window !== "undefined") return path;
  if (process.env.NEXT_PUBLIC_URL)
    return `https://${process.env.NEXT_PUBLIC_URL}${path}`;
  return `http://localhost:${process.env.PORT ?? 3000}${path}`;
}

export const createQueryString = (name: string, value: string) => {
  const params = new URLSearchParams();
  params.set(name, value);
  return params.toString();
};

export function scrollToQuestion(questionId: string) {
  const element = document.getElementById(`question-${questionId}`);
  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "center" });
    // Optional: add highlight effect
    element.classList.add("highlight-bg");
    setTimeout(() => {
      element.classList.remove("highlight-bg");
    }, 2000);
  }
}

export function getPlaceholderByContext(context: SearchContext) {
  switch (context) {
    case "global":
      return "Search for anything";
    case "study-set":
      return "Search for a question";
    case "browse":
      return "Search for study sets";
  }
}

export function determineQuestionStatus(
  consecutiveCorrectAnswers: number,
): QuestionStatus {
  // Check for no attempts or all attempts being incorrect (if numAttempts > 0 but consecutiveCorrectAnswers = 0).

  // Directly use consecutiveCorrectAnswers to determine the status.
  if (consecutiveCorrectAnswers >= 3) {
    return "Mastered";
  }
  if (consecutiveCorrectAnswers === 2) {
    return "Familiar";
  }
  if (consecutiveCorrectAnswers === 1) {
    return "Still Learning";
  }
  // This else block might be redundant due to the initial check but is kept for logical completeness.
  return "Unfamiliar";
}

/**
 * Determines which question type should be displayed based on question properties and app state
 * @param {Object} question - The current question object
 * @param {number} consecutiveCorrectAnswers - Number of consecutive correct answers
 * @param {string} mode - Current mode of the application ("multiple_choice", "written", or "combo")
 * @returns {string} - Returns "multiple_choice", "written", "flashcards", or "none"
 */
export const getQuestionType = (
  question: RouterOutputs["questions"]["getWithOptions"]["questions"][0],
  consecutiveCorrectAnswers: number,
  mode: string,
) => {
  if (mode === "flashcards") {
    return "flashcards";
  }
  if (mode === "voice") {
    return "voice";
  }
  // Case 1: Explicitly set to multiple choice mode
  if (mode === "multiple_choice") {
    return "multiple_choice";
  }

  // Case 2: Explicitly set to written mode
  if (mode === "written") {
    return "written";
  }

  // Case 3: Show multiple choice when options exist and user hasn't gotten correct answers yet
  if (
    question.questionOptions &&
    question.questionOptions.length > 0 &&
    consecutiveCorrectAnswers < 1
  ) {
    return "multiple_choice";
  }

  // Case 4: Show written question when user has gotten consecutive correct answers
  if (consecutiveCorrectAnswers >= 1) {
    return "written";
  }

  // Default case: No question to show
  return "none";
};

/**
 * Determines if the BottomNavigation component should be displayed
 * @param isCompleted - Whether the quiz/exercise is completed
 * @param isCorrect - Whether the current answer is correct (null if not answered)
 * @param questionType - The type of question ("multiple_choice", "written", "flashcards", etc.)
 * @returns boolean - Whether the BottomNavigation should be displayed
 */
export const shouldShowBottomNavigation = (
  isCompleted: boolean,
  isCorrect: boolean | null,
  questionType: string,
): boolean => {
  // Don't show if completed or not yet answered or in flashcards mode
  if (
    isCompleted ||
    isCorrect === null ||
    questionType === "flashcards" ||
    questionType === "voice"
  ) {
    return false;
  }

  // Show if answer is incorrect or in written mode
  return !isCorrect || questionType === "written";
};

export function sanitizeFileName(fileName: string) {
  // 1. Trim leading and trailing whitespace
  let sanitized = fileName.trim();

  // 2. Replace spaces with underscores
  sanitized = sanitized.replace(/\s+/g, "_");

  // 3. Normalize and remove diacritics
  sanitized = sanitized.normalize("NFD").replace(/\p{Diacritic}/gu, "");

  // 4. Remove unsupported special characters, keep periods, hyphens, underscores, and other safe characters
  sanitized = sanitized.replace(/[^a-zA-Z0-9!._-]/g, "");

  // 5. Remove consecutive periods
  sanitized = sanitized.replace(/\.{2,}/g, ".");

  // 6. Ensure the name does not start or end with a period, hyphen, or underscore
  sanitized = sanitized.replace(/^[._-]+|[._-]+$/g, "");

  return sanitized;
}

export function constructMetadata({
  title = {
    default: "Studygenie | AI Quizzes & Flashcards",
    template: "%s | Studygenie",
  },
  description = "Studygenie is the fastest way to study, ever. Upload your files and our AI will generate addictive quizzes and flashcards in seconds. Sign up 100% free.",
  applicationName = "Studygenie",
  image = "/thumbnail.png",
  noIndex = false,
  keywords = [
    "AI quizzing",
    "study aid",
    "test preparation",
    "artificial intelligence learning",
    "smart study tools",
    "adaptive quizzes",
    "personalized learning",
    "educational technology",
    "exam preparation",
    "memory enhancement",
    "spaced repetition",
    "flashcards",
    "neuroscience-based learning",
    "efficient studying",
    "academic performance",
    "cognitive science",
    "e-learning",
    "interactive quizzes",
    "student productivity",
    "knowledge retention",
    "self-assessment tools",
    "AI-powered education",
    "study optimization",
    "smart flashcards",
    "automated quiz generation",
  ],
}: {
  title?: {
    default: string;
    template: string;
  };
  description?: string;
  applicationName?: string;
  image?: string;
  icons?: string;
  noIndex?: boolean;
  keywords?: string[];
} = {}): Metadata {
  return {
    title,
    description,
    generator: "Next.js",
    applicationName: applicationName,
    keywords,
    openGraph: {
      title,
      description,
      images: [
        {
          url: image,
        },
      ],
    },
    twitter: {
      card: "summary_large_image",
      title,
      description,
      images: [image],
      // creator: "@Keaanm",
    },
    metadataBase: new URL("https://app.studygenie.io"),
    ...(noIndex && {
      robots: {
        index: false,
        follow: false,
      },
    }),
  };
}

// TODO: move to zod
export function isValidUUID(uuid: string) {
  const uuidSchema = z.uuidv4();
  const parsedId = uuidSchema.safeParse(uuid);
  if (!parsedId.success) {
    return false;
  }
  return true;
}

export function splitPages(text: string) {
  const maxChars = 6000;

  if (text.length > maxChars) {
    return text.split("///Page-Break///");
  }
  return [text];
}

// Fisher-Yates shuffle algorithm
export function shuffleArray<T>(array: T[]): T[] {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

export function flagIfAnswerIsTooLong(question: {
  questions: {
    id: string;
    type: "written" | "multiple_choice";
    createdAt: string;
    userId: string;
    studySetId: string;
    question: string;
    answer: string;
    fileId: string | null;
  };
  questionOptions: {
    id: string;
    question_id: string;
    option_text: string;
    is_correct: boolean;
  }[];
}) {
  const correctAnswerLength = question.questions.answer.length;

  // Iterate over each distractor
  for (const distractor of question.questionOptions) {
    if (distractor.is_correct) continue;
    const distractorLength = distractor.option_text.length;

    // Calculate the 20% threshold
    const threshold = distractorLength * 1.2;

    // Check if the correct answer is longer than the threshold
    if (correctAnswerLength > threshold) {
      return true; // Flag the question
    }
  }

  return false; // Do not flag the question
}

export const titleToSlug = (name: string) => {
  const slug = slugify(name, {
    lower: true, // Convert to lowercase
    strict: true, // Remove special characters
    replacement: "-", // Replace spaces with hyphens
  });

  return encodeURI(slug);
};

export const getSlug = (name: string, id: string) => {
  return `${titleToSlug(name)}-${id}`;
};

export const getIdFromSlug = (slug: string) => {
  // v4-strict: matches what the API's z.uuidv4() validators accept. Slugs
  // with a non-v4 trailing UUID (sitemap stragglers, mangled crawler URLs,
  // mistyped share links) get null here so callers can notFound() without
  // round-tripping a guaranteed-failing tRPC call.
  const match = slug.match(
    /([a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12})$/i,
  );
  const id = match ? match[1] : null;
  return id;
};

export const processSystemSpeech = (input: string): string => {
  if (!input) {
    return "";
  }
  let output = input;

  // dont include code blocks, removing anything starting with ``` and starting again with the next ```
  const codeBlockRegex = /```[\s\S]*?```/gu;
  output = output.replace(codeBlockRegex, "");

  return output;
};

export type GetAudioContextOptions = AudioContextOptions & {
  id?: string;
};

const map: Map<string, AudioContext> = new Map();

export const audioContext: (
  options?: GetAudioContextOptions,
) => Promise<AudioContext> = (() => {
  // const didInteract = new Promise((res) => {
  //   window.addEventListener("pointerdown", res, { once: true });
  //   window.addEventListener("keydown", res, { once: true });
  // });

  return async (options?: GetAudioContextOptions) => {
    try {
      const a = new Audio();
      a.src =
        "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";
      await a.play();
      if (options?.id && map.has(options.id)) {
        const ctx = map.get(options.id);
        if (ctx) {
          return ctx;
        }
      }
      const ctx = new AudioContext(options);
      if (options?.id) {
        map.set(options.id, ctx);
      }
      return ctx;
    } catch (_e) {
      // await didInteract;
      if (options?.id && map.has(options.id)) {
        const ctx = map.get(options.id);
        if (ctx) {
          return ctx;
        }
      }
      const ctx = new AudioContext(options);
      if (options?.id) {
        map.set(options.id, ctx);
      }
      return ctx;
    }
  };
})();

export function base64ToArrayBuffer(base64: string) {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}
